import { useState, useEffect, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { billRefLabel, type BillRef } from '../../../../shared/contracts/bill-ref.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';
import { personLabel } from '../../components/ApprovalLevels.js';

/** A `GET /v1/payments/eligibility/status/:status` row (T173 + bill / vendor references). */
interface EligibleClaim {
  readonly eligibilityId: string;
  /** The job, dealer and date the bill is known by. */
  readonly bill: BillRef | null;
  readonly vendorName: string | null;
  readonly vendorCode: string | null;
  readonly grossAmount: number;
  readonly paymentStatusCode: string;
}

/** L15.01 — Finance Maker Queue: list of claims ready for payment voucher creation. */
export function MakerQueuePage(): ReactNode {
  const { t } = useTranslation();
  // Amounts follow the tenant's DISPLAY_NUMBER_FORMAT, not a locale in the source.
  const { money } = useBusinessNumber();
  const { day } = useBusinessDay();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState<EligibleClaim[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiRequest<EligibleClaim[]>('/v1/payments/eligibility/status/PAYMENT_ELIGIBLE', { token })
      .then(setItems)
      .catch(e => setError((e as Error).message))
      .finally(() => setLoading(false));
  }, [token]);

  return (
    <div style={{ padding: 24 }}>
      <h1>{t('finance.maker.title')}</h1>
      {error && <p style={{ color: 'red' }}>{error}</p>}
      {loading && <p>{t('finance.maker.loading')}</p>}
      {!loading && items.length === 0 && <p>{t('finance.maker.empty')}</p>}
      {items.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: 'var(--color-bg-muted)', borderBottom: '1px solid var(--color-border-default)' }}>
              <th style={{ padding: 8, textAlign: 'left' }}>{t('finance.maker.colClaim')}</th>
              <th style={{ padding: 8, textAlign: 'left' }}>{t('finance.maker.colVendor')}</th>
              <th style={{ padding: 8, textAlign: 'right' }}>{t('finance.maker.colAmount')}</th>
              <th style={{ padding: 8, textAlign: 'left' }}>{t('finance.maker.colStatus')}</th>
              <th style={{ padding: 8 }}>{t('finance.maker.colAction')}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.eligibilityId} style={{ borderBottom: '1px solid var(--color-border-default)' }}>
                <td style={{ padding: 8 }}>{billRefLabel(item.bill, day)}</td>
                <td style={{ padding: 8 }}>{personLabel(item.vendorName, item.vendorCode) ?? '—'}</td>
                <td style={{ padding: 8, textAlign: 'right' }}>{money(item.grossAmount)}</td>
                <td style={{ padding: 8 }}>{item.paymentStatusCode}</td>
                <td style={{ padding: 8, textAlign: 'center' }}>
                  <button onClick={() => navigate(`/finance/maker/vouchers/new?eligibilityId=${item.eligibilityId}`)} style={{ padding: '4px 12px', background: 'var(--color-text-link)', color: 'var(--color-text-inverse)', border: 'none', borderRadius: 4, cursor: 'pointer' }}>{t('finance.maker.createVoucher')}</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
